/* eslint-disable react/no-unstable-nested-components */
import React, { Fragment } from 'react';
import { format, parse } from 'date-fns';
import { Tooltip } from '@material-ui/core';
import { connect } from 'react-redux';
import classnames from 'classnames';
import styles from './styles.scss';
import EmptyShipmentEvents from './EmptyShipmentEvents';
import ShipmentEvent from './ShipmentEvent';

const deliveredStates = [
  'delivered',
  'delivered_damaged',
  'delivered_to_neighbour',
  'delivered_to_reception',
  'delivered_to_safe_location',
];

export const textTruncate = (text) => `${text.substring(0, 26)}...`;

const outForDeliveryStates = ['out_for_delivery'];

function DeliveryStateHeading({ shipment, fontFamily }) {
  const deliveredDateLabel = 'Delivered date';
  const estimatedDeliveryLabel = 'Estimated delivery';
  let displayLabel = '';

  if (shipment && shipment.shipmentState) {
    if (deliveredStates.includes(shipment.shipmentState.state)) {
      displayLabel = deliveredDateLabel;
    } else {
      displayLabel = estimatedDeliveryLabel;
    }
  }
  return (
    <p className={styles.estimated} style={fontFamily}>
      {displayLabel}
    </p>
  );
}

function TimeComponent({ dateTime, style }) {
  const parsedDate = parse(dateTime);
  return (
    <div className={styles.dateTime}>
      <div className={styles.largeNum}>
        <p>{format(parsedDate, 'DD')}</p>
      </div>
      <div className={styles.monthWeekDay}>
        <p className={styles.month} style={style}>
          {format(parsedDate, 'MMM')}
        </p>
        <p className={styles.day} style={style}>
          /{format(parsedDate, 'dddd')}
        </p>
      </div>
    </div>
  );
}

function DeliverySlot({ startDate, endDate }) {
  const parsedStartDate = parse(startDate);
  const parsedEndDate = parse(endDate);
  const textToDisplay = `${format(parsedStartDate, 'HH:mm')} - ${format(parsedEndDate, 'HH:mm')}`;
  return (
    <Tooltip title={textToDisplay} placement="right">
      <div>between {textToDisplay}</div>
    </Tooltip>
  );
}

function getShipmentEventComponents(events, fontFamily) {
  if (events && events.length === 0) {
    return (
      <div className={styles.trackingItemList}>
        <EmptyShipmentEvents />
      </div>
    );
  }
  return (
    <div className={styles.trackingItemList}>
      {events.map((event, index) => (
        <ShipmentEvent
          key={event.renderKey}
          data={event}
          isLast={index === events.length - 1}
          fontFamily={fontFamily}
        />
      ))}
    </div>
  );
}

function CarrierManagement({
  isCarrierLinkActive, trackingReference, carrierTrackingPage,
}) {
  let truncatedText = trackingReference;
  if (trackingReference.length > 26) {
    truncatedText = textTruncate(trackingReference);
  }
  return (
    <div className={styles.carrierManage}>
      <div className={styles.carrierMeta}>
        <div>
          <strong>Tracking number: </strong>
        </div>
        <span>
          {isCarrierLinkActive && carrierTrackingPage ? (
            <Tooltip title={trackingReference}>
              <a href={carrierTrackingPage} target="_blank" rel="noopener noreferrer">
                {truncatedText}
              </a>
            </Tooltip>
          ) : (<Tooltip title={trackingReference}><div>{truncatedText}</div></Tooltip>)}
        </span>
      </div>
    </div>
  );
}

function ShipmentDetails({ shipment, fontFamily, isCarrierLinkActive }) {
  const trackingComponentItems = getShipmentEventComponents([...shipment?.events], fontFamily);
  const trackingRefStyle = {
    ...fontFamily,
    marginBottom: shipment.signee ? '2px' : '10px',
  };

  function DateWidget() {
    if (deliveredStates.includes(shipment.shipmentState.state) && shipment.deliveredDate) {
      return <TimeComponent dateTime={shipment.deliveredDate} style={fontFamily} />;
    }
    if (shipment.deliverySlotInterval) {
      return <TimeComponent dateTime={shipment.deliverySlotInterval.end} style={fontFamily} />;
    }
    return <TimeComponent dateTime={shipment.expectedDeliveryDate} style={fontFamily} />;
  }
  const renderSignSection = () => {
    let signedBy = shipment.signee;
    if (shipment.signee.length > 26) {
      signedBy = textTruncate(shipment.signee);
    }
    return (
      <Tooltip title={shipment.signee}>
        <div className={styles.signee} style={fontFamily}>
          <strong>Signed by: </strong>
          <span className={styles.signedBy}>{signedBy}</span>
        </div>
      </Tooltip>
    );
  };

  return (
    <>
      <div className={styles.accentBlock}>
        {shipment && (shipment.deliveredDate || shipment.deliverySlotInterval || shipment.expectedDeliveryDate) && (
          <>
            <DeliveryStateHeading shipment={shipment} fontFamily={fontFamily} />
            <DateWidget shipment={shipment} fontFamily={fontFamily} />
          </>
        )}
        <p className={classnames(styles.stat, styles.flexTruncate)} style={fontFamily}>
          <span className={styles.textTruncate}>
            {shipment && shipment.shipmentState && shipment.shipmentState.stateLabel}
          </span>
        </p>
        {outForDeliveryStates.includes(shipment.shipmentState.state) && shipment.deliverySlotInterval && (
          <div className={classnames(styles.stat, styles.flexTruncate)} style={fontFamily}>
            <span className={styles.textTruncate}>
              <DeliverySlot
                startDate={shipment.deliverySlotInterval.start}
                endDate={shipment.deliverySlotInterval.end}
              />
            </span>
          </div>
        )}
      </div>
      <div className={styles.ref} style={trackingRefStyle}>
        {shipment && shipment.trackingReference && (
          <CarrierManagement
            isCarrierLinkActive={isCarrierLinkActive}
            trackingReference={shipment.trackingReference}
            carrierTrackingPage={shipment.carrierTrackingPage}
            carrierName={shipment.carrier}
          />
        )}
      </div>
      {shipment.signee && renderSignSection()}
      {trackingComponentItems}
    </>
  );
}

const mapStateToProps = ({
  trackingPage: {
    trackingPageData: { font: trackingPageFont, isCarrierLinkActive },
  },
}) => ({
  trackingPageFont,
  isCarrierLinkActive,
});

export default connect(mapStateToProps)(ShipmentDetails);
